import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Truck, ShieldCheck, Package, Tag, X } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const field = "w-full bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-white focus:border-[#FF3B30] outline-none transition-colors";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paypal, setPaypal] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", address: "", city: "", state: "", zip: "", country: "USA",
  });
  const [shipping, setShipping] = useState(6.99);
  const [shippingLive, setShippingLive] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [applyingCode, setApplyingCode] = useState(false);

  const paypalOptions = useMemo(
    () => (paypal ? { clientId: paypal.client_id, currency: "USD", intent: "capture" } : null),
    [paypal]
  );

  useEffect(() => {
    api.get("/paypal/config").then((r) => { if (r.data.enabled) setPaypal(r.data); }).catch(() => {});
    api.get("/payments/config").then((r) => setStripeEnabled(!!r.data.stripe_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.city || !form.zip || items.length === 0) { setShippingLive(false); return; }
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity })),
      shipping: { country: form.country, state: form.state, city: form.city, zip: form.zip, address: form.address },
    };
    setShippingLoading(true);
    const t = setTimeout(() => {
      api.post("/checkout/shipping-rate", payload)
        .then((r) => { setShipping(r.data.shipping); setShippingLive(!!r.data.live); })
        .catch(() => {})
        .finally(() => setShippingLoading(false));
    }, 600);
    return () => { clearTimeout(t); setShippingLoading(false); };
  }, [form.city, form.zip, form.state, form.country, form.address, items]);

  if (items.length === 0) { navigate("/cart"); return null; }

  const formValid = form.name && form.email && form.address && form.city && form.zip;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const itemsPayload = items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity }));

  const discountAmount = discount
    ? (discount.type === "percent"
        ? Math.min(subtotal, subtotal * discount.value / 100)
        : discount.type === "fixed"
          ? Math.min(subtotal, discount.value)
          : 0)
    : 0;
  const total = subtotal - discountAmount + shipping;

  const applyCode = async () => {
    const code = discountCode.trim();
    if (!code) return;
    setApplyingCode(true);
    try {
      const { data } = await api.post("/discount/validate", { code, subtotal });
      if (data.valid) { setDiscount({ code: data.code, type: data.type, value: data.value }); toast.success(data.message); }
      else { setDiscount(null); toast.error(data.message); }
    } catch { toast.error("Could not validate code"); }
    finally { setApplyingCode(false); }
  };
  const removeCode = () => { setDiscount(null); setDiscountCode(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) { toast.error("Please fill in all required fields"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        items: itemsPayload, shipping: form, origin_url: window.location.origin, discount_code: discount?.code || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  const createPaypalOrder = async () => {
    if (!formValid) { toast.error("Fill in your shipping details first"); throw new Error("form invalid"); }
    const { data } = await api.post("/paypal/create-order", {
      items: itemsPayload, shipping: form, origin_url: window.location.origin, discount_code: discount?.code || null,
    });
    return data.paypal_order_id;
  };

  const onPaypalApprove = async (data) => {
    try {
      const { data: res } = await api.post(`/paypal/capture/${data.orderID}`);
      if (res.status === "COMPLETED") {
        clearCart();
        navigate(`/order/success?order_id=${res.order_id}&method=paypal`);
      } else {
        toast.error("Payment not completed. Please try again.");
      }
    } catch {
      toast.error("Payment capture failed.");
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-12" data-testid="checkout-page">
      <h1 className="font-display text-5xl uppercase tracking-tighter mb-10">Check<span className="text-[#FF3B30]">out</span></h1>
      <div className="grid lg:grid-cols-2 gap-10">
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-2">Shipping Details</h2>
          {!user && <p className="text-xs text-zinc-500 uppercase tracking-widest">Checking out as guest — <a href="/login" className="text-[#FF3B30]">login</a> to save your order.</p>}
          <input className={field} placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid="checkout-name" />
          <input className={field} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="checkout-email" />
          <input className={field} placeholder="Street Address *" value={form.address} onChange={set("address")} data-testid="checkout-address" />
          <div className="grid grid-cols-2 gap-4">
            <input className={field} placeholder="City *" value={form.city} onChange={set("city")} data-testid="checkout-city" />
            <input className={field} placeholder="State" value={form.state} onChange={set("state")} data-testid="checkout-state" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input className={field} placeholder="ZIP *" value={form.zip} onChange={set("zip")} data-testid="checkout-zip" />
            <input className={field} placeholder="Country" value={form.country} onChange={set("country")} data-testid="checkout-country" />
          </div>
          {stripeEnabled && (
            <>
              <button type="submit" disabled={loading} data-testid="pay-button" className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                <Lock size={16} /> {loading ? "Redirecting…" : `Pay with Card · $${total.toFixed(2)}`}
              </button>
              <p className="text-center text-xs text-zinc-500">Secure card payment via Stripe</p>
            </>
          )}

          {paypal && (
            <div className="pt-2" data-testid="paypal-section">
              {stripeEnabled && (
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-zinc-800" />
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">or</span>
                  <div className="flex-1 h-px bg-zinc-800" />
                </div>
              )}
              {!formValid && <p className="text-center text-xs text-[#EAB308] mb-3">Fill in shipping details above to enable PayPal</p>}
              <div className={!formValid ? "opacity-40 pointer-events-none" : ""}>
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                    disabled={!formValid}
                    createOrder={createPaypalOrder}
                    onApprove={onPaypalApprove}
                    onError={() => toast.error("PayPal error. Please try again.")}
                  />
                </PayPalScriptProvider>
              </div>
            </div>
          )}
        </form>

        <div className="border border-zinc-800 p-6 h-fit bg-zinc-900">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-6">Order</h2>
          <div className="space-y-3 mb-6">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3 items-center">
                <img src={i.image} alt={i.name} className="h-14 w-14 object-cover border border-zinc-800" />
                <div className="flex-1 text-sm">
                  <div className="font-bold uppercase tracking-tight leading-tight">{i.name}</div>
                  <div className="text-xs text-zinc-500">{[i.color, i.size].filter(Boolean).join(" · ")}{(i.color || i.size) ? " · " : ""}Qty {i.quantity}</div>
                </div>
                <div className="text-sm font-bold">${(i.price * i.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-800 pt-4 mb-4" data-testid="discount-box">
            {discount ? (
              <div className="flex items-center justify-between bg-zinc-950 border border-green-500/40 px-3 py-2.5" data-testid="discount-applied">
                <span className="flex items-center gap-2 text-sm text-green-400"><Tag size={14} /> {discount.code} applied</span>
                <button onClick={removeCode} data-testid="remove-discount" className="text-zinc-500 hover:text-[#FF3B30] transition-colors"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                  placeholder="Discount code" data-testid="discount-input"
                  className="flex-1 bg-zinc-950 border border-zinc-700 px-3 py-2.5 text-sm text-white uppercase focus:border-[#FF3B30] outline-none transition-colors" />
                <button onClick={applyCode} disabled={applyingCode} data-testid="apply-discount"
                  className="border border-zinc-700 text-xs font-bold uppercase tracking-widest px-4 hover:border-white transition-colors disabled:opacity-50">
                  {applyingCode ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>
          <div className="space-y-2 text-sm border-t border-zinc-800 pt-4">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-green-400" data-testid="checkout-discount-row">
                <span>Discount ({discount.code})</span><span data-testid="checkout-discount-amount">−${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-zinc-400" data-testid="checkout-shipping-row">
              <span>Shipping{shippingLive && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#22C55E]">Live</span>}</span>
              <span className="text-white" data-testid="checkout-shipping-amount">
                {shippingLoading ? "Calculating…" : `$${shipping.toFixed(2)}`}
              </span>
            </div>
            {!form.city || !form.zip ? (
              <p className="text-[11px] text-zinc-500">Enter your city & ZIP to calculate exact shipping.</p>
            ) : shippingLive ? (
              <p className="text-[11px] text-zinc-500">Real-time rate via Printful.</p>
            ) : null}
            <div className="flex justify-between font-display text-xl pt-2"><span>Total</span><span className="text-[#FF3B30]" data-testid="checkout-total">${total.toFixed(2)}</span></div>
          </div>

          <div className="mt-6 pt-5 border-t border-zinc-800 space-y-3" data-testid="trust-badges">
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              <ShieldCheck size={16} className="text-[#22C55E] shrink-0" />
              <span>Secure encrypted checkout — powered by Stripe & PayPal</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              <Package size={16} className="text-[#FF3B30] shrink-0" />
              <span>Printed on demand & shipped via Printful</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              <Truck size={16} className="text-white shrink-0" />
              <span>Order tracking emailed the moment it ships</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
